export function parseInquiryContext(search = '') {
  const params = new URLSearchParams(search);
  return {
    product: params.get('product')?.trim() || '',
    productUrl: params.get('product_url')?.trim() || '',
    store: params.get('store')?.trim() || '',
    intent: params.get('intent')?.trim() || '',
  };
}

export function buildInquiryUrl(baseUrl, context = {}, origin = 'https://example.test') {
  if (!baseUrl) return '';
  const url = new URL(baseUrl, origin);
  for (const [key, value] of Object.entries(context)) {
    if (value !== undefined && value !== null && String(value).trim()) {
      url.searchParams.set(key, String(value).trim());
    }
  }
  return url.pathname + url.search + url.hash;
}

function setupNavigation() {
  document.querySelectorAll('[data-mobile-nav]').forEach((navigation) => {
    const summary = navigation.querySelector('summary');
    const close = navigation.querySelector('[data-mobile-nav-close]');
    let pendingCloseFocus = null;
    const clearPendingCloseFocus = () => {
      if (!pendingCloseFocus) return;
      navigation.removeEventListener('toggle', pendingCloseFocus);
      pendingCloseFocus = null;
    };
    if (!(navigation instanceof HTMLDetailsElement) || !summary) return;
    const closeNavigation = () => {
      if (!navigation.open) return;
      clearPendingCloseFocus();
      const focusOrigin = document.activeElement;
      const shouldReturnFocus = navigation.contains(focusOrigin);
      pendingCloseFocus = () => {
        clearPendingCloseFocus();
        if (navigation.open || !shouldReturnFocus || document.activeElement !== focusOrigin) return;
        summary.focus();
      };
      navigation.addEventListener('toggle', pendingCloseFocus, { once: true });
      navigation.open = false;
    };
    navigation.addEventListener('click', (event) => {
      if (event.target.closest('a')) navigation.removeAttribute('open');
    });
    close?.addEventListener('click', closeNavigation);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && navigation.open) {
        closeNavigation();
      }
    });
  });

  // Desktop nav disclosures. <details> already gives keyboard operation and a no-JS fallback; this
  // adds what a bare disclosure does not have: one panel open at a time, pointer hover-intent on
  // fine pointers, an animated close (a <details> closes instantly on its own), Arrow Down into the
  // panel, Escape back to the toggle, and close on leaving.
  const drops = Array.from(document.querySelectorAll('.primary-nav [data-nav-drop]'));
  if (!drops.length) return;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CLOSE_MS = 140;

  const closeTimers = new WeakMap();

  const cancelClose = (drop) => {
    window.clearTimeout(closeTimers.get(drop));
    closeTimers.delete(drop);
    drop.classList.remove('is-closing');
  };

  const openDrop = (drop) => {
    drops.forEach((other) => {
      if (other !== drop) closeDrop(other, true);
    });
    // Re-entering during the closing animation must cancel the pending close, or the timer fires
    // afterwards and shuts a panel the pointer is already inside.
    cancelClose(drop);
    drop.open = true;
  };

  function closeDrop(drop, immediate) {
    if (!drop.open) return;
    if (immediate || reduceMotion.matches) {
      cancelClose(drop);
      drop.open = false;
      return;
    }
    if (closeTimers.has(drop)) return;
    // Play the closing animation first, then actually close. The timer is the authority so a
    // dropped animationend (tab hidden, element removed) can never leave the panel stuck open.
    drop.classList.add('is-closing');
    closeTimers.set(drop, window.setTimeout(() => {
      closeTimers.delete(drop);
      drop.classList.remove('is-closing');
      drop.open = false;
    }, CLOSE_MS));
  }

  drops.forEach((drop) => {
    const item = drop.closest('[data-nav-item]');
    const toggle = drop.querySelector('summary');
    const panel = drop.querySelector('[data-nav-panel]');

    const syncDropState = () => {
      toggle?.setAttribute('aria-expanded', String(drop.open));
    };
    syncDropState();

    // Wide panels (Sell or Trade has five columns) can run past the viewport edge from their
    // anchor. Shift with the independent `translate` property so the open animation's transform
    // is untouched, keeping the panel 16px inside the window.
    const keepPanelInView = () => {
      if (!panel) return;
      panel.style.translate = '';
      const rect = panel.getBoundingClientRect();
      const edge = 16;
      let shift = 0;
      if (rect.left < edge) shift = edge - rect.left;
      else if (rect.right > window.innerWidth - edge) shift = window.innerWidth - edge - rect.right;
      if (shift) panel.style.translate = `${Math.round(shift)}px 0`;
    };

    drop.addEventListener('toggle', () => {
      syncDropState();
      if (drop.open) {
        keepPanelInView();
        drops.forEach((other) => {
          if (other !== drop) closeDrop(other, true);
        });
      }
    });

    // A summary toggles natively on click, which would re-open a panel the animation is closing.
    toggle?.addEventListener('click', (event) => {
      if (!drop.open) return;
      event.preventDefault();
      // Dismissing on purpose has to stick: the pointer is still over the item, so without this
      // the next pointerenter (any jitter across the edge) re-opens what was just closed.
      drop.dataset.dismissed = 'true';
      closeDrop(drop);
    });

    drop.addEventListener('click', (event) => {
      if (event.target.closest('a')) drop.open = false;
    });

    drop.addEventListener('focusout', (event) => {
      // relatedTarget is null when focus goes nowhere - a mouse press on panel padding or on a
      // group title. That is not "focus left the panel", and closing on it shut the panel under
      // the pointer across most of its surface. Only a real next focus target closes it.
      if (drop.open && event.relatedTarget && !drop.contains(event.relatedTarget)) closeDrop(drop, true);
    });

    toggle?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      openDrop(drop);
      window.requestAnimationFrame(() => panel?.querySelector('a')?.focus());
    });

    if (!item) return;
    let hoverTimer = 0;
    item.addEventListener('pointerenter', () => {
      if (!finePointer.matches || drop.dataset.dismissed) return;
      window.clearTimeout(hoverTimer);
      hoverTimer = window.setTimeout(() => openDrop(drop), 90);
    });
    item.addEventListener('pointerleave', () => {
      if (!finePointer.matches) return;
      delete drop.dataset.dismissed;
      window.clearTimeout(hoverTimer);
      hoverTimer = window.setTimeout(() => closeDrop(drop), 220);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = drops.find((drop) => drop.open);
    if (!open) return;
    open.dataset.dismissed = 'true';
    closeDrop(open, true);
    open.querySelector('summary')?.focus();
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-nav-drop]')) return;
    drops.forEach((drop) => closeDrop(drop, true));
  });
}

function setupDialogs() {
  if (typeof HTMLDialogElement === 'undefined') return;
  document.querySelectorAll('[data-dialog-open]').forEach((trigger) => {
    const dialog = document.getElementById(trigger.dataset.dialogOpen);
    if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      dialog.showModal();
    });
    dialog.querySelector('[data-dialog-close]')?.addEventListener('click', () => {
      dialog.close();
      trigger.focus();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  });
}

function setupFacets() {
  const button = document.querySelector('[data-facets-toggle]');
  const facets = document.querySelector('[data-facets]');
  if (!button || !facets) return;
  button.addEventListener('click', () => {
    const open = facets.classList.toggle('is-open');
    button.setAttribute('aria-expanded', String(open));
  });
}

function setupInquiryContext() {
  const form = document.querySelector('[data-inquiry-form]');
  if (!form) return;
  const context = parseInquiryContext(window.location.search);
  const assignments = {
    '[data-context-product]': context.product,
    '[data-context-product-url]': context.productUrl,
    '[data-context-store]': context.store,
    '[data-context-intent]': context.intent,
  };

  for (const [selector, value] of Object.entries(assignments)) {
    const input = form.querySelector(selector);
    if (input && value) input.value = value;
  }

  const summary = document.querySelector('[data-context-summary]');
  const summaryText = document.querySelector('[data-context-summary-text]');
  const parts = [context.product, context.store].filter(Boolean);
  if (summary && summaryText && parts.length) {
    summaryText.textContent = parts.join(' — ');
    summary.hidden = false;
  }
}

function setupInquiryValidation() {
  const form = document.querySelector('[data-inquiry-form]');
  if (!form) return;
  const requiredFields = form.querySelectorAll('[required][data-validation-error]');

  const updateFieldError = (field) => {
    const error = document.getElementById(field.dataset.validationError);
    if (!error) return;
    if (field.validity.valid) {
      field.removeAttribute('aria-invalid');
      error.textContent = '';
      error.hidden = true;
      return;
    }
    const label = field.dataset.validationLabel || 'This field';
    error.textContent = field.validity.valueMissing ? `${label} is required.` : field.validationMessage;
    error.hidden = false;
    field.setAttribute('aria-invalid', 'true');
  };

  form.addEventListener('invalid', (event) => {
    if (event.target.matches?.('[required][data-validation-error]')) updateFieldError(event.target);
  }, true);
  requiredFields.forEach((field) => {
    field.addEventListener('input', () => {
      if (field.hasAttribute('aria-invalid')) updateFieldError(field);
    });
  });
}

function setupProductForm() {
  const form = document.querySelector('[data-product-form]');
  const variantSelect = form?.querySelector('[data-variant-select]');
  if (!form || !variantSelect) return;
  const submit = form.querySelector('[type="submit"]');
  const price = document.querySelector('[data-product-price]');
  const availability = document.querySelector('[data-product-availability]');

  const update = () => {
    const option = variantSelect.selectedOptions[0];
    if (!option) return;
    const available = option.dataset.available === 'true';
    if (price) price.textContent = option.dataset.price;
    if (submit) {
      submit.disabled = !available;
      submit.textContent = available ? form.dataset.addLabel : form.dataset.soldLabel;
    }
    if (availability) availability.textContent = available ? form.dataset.availableLabel : form.dataset.soldLabel;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  };

  variantSelect.addEventListener('change', update);
}

function padPlate(value) {
  return String(value).padStart(2, '0');
}

let platePhotoSwipe = null;
let platePhotoSwipeLoading = null;
let platePhotoSwipeTrigger = null;
let platePhotoSwipeModalCleanup = null;

function activatePlatePhotoSwipeModal(root, returnFocusTo) {
  platePhotoSwipeModalCleanup?.(false);

  const backgroundState = Array.from(document.body.children)
    .filter((element) => element !== root && !element.contains(root))
    .map((element) => ({ element, wasInert: element.hasAttribute('inert') }));
  backgroundState.forEach(({ element }) => element.setAttribute('inert', ''));

  let active = true;
  const focusableSelector = [
    'a[href]',
    'area[href]',
    'button',
    'input:not([type="hidden"])',
    'select',
    'textarea',
    'iframe',
    '[contenteditable]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  const getFocusable = () => Array.from(root.querySelectorAll(focusableSelector))
    .filter((element) => element.tabIndex >= 0
      && !element.closest('[inert], [hidden], [aria-hidden="true"]')
      && element.getClientRects().length > 0
      && getComputedStyle(element).visibility !== 'hidden');
  const focusRoot = () => root.focus({ preventScroll: true });

  const onKeydown = (event) => {
    if (!active || event.key !== 'Tab') return;
    const focusable = getFocusable();
    if (!focusable.length) {
      event.preventDefault();
      focusRoot();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const focused = document.activeElement;
    if (!root.contains(focused) || focused === root) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    } else if (event.shiftKey && focused === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && focused === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  };
  const onFocusin = (event) => {
    if (active && event.target instanceof Node && !root.contains(event.target)) focusRoot();
  };

  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('focusin', onFocusin, true);
  focusRoot();

  const cleanup = (restoreFocus = true) => {
    if (!active) return;
    active = false;
    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('focusin', onFocusin, true);
    backgroundState.forEach(({ element, wasInert }) => {
      if (!wasInert) element.removeAttribute('inert');
    });
    if (platePhotoSwipeModalCleanup === cleanup) platePhotoSwipeModalCleanup = null;
    if (restoreFocus && returnFocusTo?.isConnected) returnFocusTo.focus({ preventScroll: true });
  };
  platePhotoSwipeModalCleanup = cleanup;
}

async function openPlatePhotoSwipe(trigger) {
  if (typeof window.__IVG_LOAD_PSWP__ !== 'function') return false;
  const gallery = document.querySelector('.product-media');
  const triggers = Array.from(document.querySelectorAll('[data-plate-open][data-pswp-src]'));
  if (!gallery || !triggers.length) return false;
  const index = triggers.indexOf(trigger);
  if (index < 0) return false;

  try {
    if (!platePhotoSwipe) {
      platePhotoSwipeLoading =
        platePhotoSwipeLoading ||
        (async () => {
          const { PhotoSwipeLightbox, PhotoSwipe, ariaLabel } = await window.__IVG_LOAD_PSWP__();
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const options = {
            gallery,
            children: '[data-plate-open][data-pswp-src]',
            pswpModule: PhotoSwipe,
            bgOpacity: 0.97,
            closeOnVerticalDrag: true,
            wheelToZoom: true,
            pinchToClose: true,
            // The theme restores focus after releasing inert background content on destroy.
            returnFocus: false,
            showHideAnimationType: reducedMotion ? 'none' : 'zoom',
            hideAnimationDuration: reducedMotion ? 0 : 220,
            showAnimationDuration: reducedMotion ? 0 : 220,
            easing: 'ease-out',
            arrowPrev: true,
            arrowNext: true,
            zoom: true,
            close: true,
            counter: false,
            arrowPrevTitle: 'Previous image',
            arrowNextTitle: 'Next image',
            closeTitle: 'Close image viewer',
            zoomTitle: 'Zoom image',
            mainClass: 'plate-pswp',
            paddingFn: (viewportSize) => ({
              top: 56,
              bottom: viewportSize.x < 720 ? 72 : 56,
              left: 12,
              right: 12,
            }),
          };

          const lightbox = new PhotoSwipeLightbox(options);

          lightbox.addFilter('domItemData', (itemData, element) => {
            const src = element.dataset.pswpSrc;
            if (!src) return itemData;
            itemData.src = src;
            itemData.srcset = element.dataset.pswpSrcset || '';
            itemData.width = Number(element.dataset.pswpWidth) || 0;
            itemData.height = Number(element.dataset.pswpHeight) || 0;
            itemData.w = itemData.width;
            itemData.h = itemData.height;
            const plate = element.closest('[data-product-plate]');
            const img = plate?.querySelector('.plate__frame img');
            if (img) {
              itemData.msrc = img.currentSrc || img.src;
              itemData.alt = img.alt || '';
            }
            const currentTriggers = Array.from(document.querySelectorAll('[data-plate-open][data-pswp-src]'));
            const total = currentTriggers.length;
            const plateIndex = currentTriggers.indexOf(element) + 1;
            const captionText = plate?.querySelector('[data-plate-caption-text]')?.textContent?.trim() || '';
            itemData.plateLabel = `${padPlate(plateIndex)} / ${padPlate(total)}`;
            itemData.caption = captionText;
            return itemData;
          });

          lightbox.addFilter('thumbEl', (_thumbEl, data) => {
            const plate = data.element?.closest('[data-product-plate]');
            return plate?.querySelector('.plate__frame img') || _thumbEl;
          });

          lightbox.on('uiRegister', () => {
            lightbox.pswp.ui.registerElement({
              name: 'plate-caption',
              order: 9,
              isButton: false,
              appendTo: 'root',
              onInit: (el, pswp) => {
                el.className = 'pswp__plate-caption';
                el.setAttribute('aria-live', 'polite');
                const number = document.createElement('span');
                number.className = 'plate__number pswp__plate-number';
                const text = document.createElement('span');
                text.className = 'plate__text pswp__plate-text';
                el.append(number, text);
                const update = () => {
                  const data = pswp.currSlide?.data || {};
                  number.textContent = data.plateLabel || '';
                  text.textContent = data.caption || '';
                  text.hidden = !data.caption;
                };
                pswp.on('change', update);
                update();
              },
            });
          });

          lightbox.on('beforeOpen', () => {
            document.documentElement.setAttribute('data-pswp-active', '');
          });

          const labelRoot = () => {
            const root = lightbox.pswp?.element;
            if (!root) return;
            root.setAttribute('role', 'dialog');
            root.setAttribute('aria-modal', 'true');
            if (ariaLabel) root.setAttribute('aria-label', ariaLabel);
          };
          // firstUpdate fires for every open, including reduced-motion (no animation) opens.
          lightbox.on('firstUpdate', () => {
            labelRoot();
            const root = lightbox.pswp?.element;
            if (root) activatePlatePhotoSwipeModal(root, platePhotoSwipeTrigger);
          });
          lightbox.on('openingAnimationStart', labelRoot);

          lightbox.on('destroy', () => {
            platePhotoSwipeModalCleanup?.();
            platePhotoSwipeTrigger = null;
            document.documentElement.removeAttribute('data-pswp-active');
          });

          return lightbox;
        })();
      platePhotoSwipe = await platePhotoSwipeLoading;
    }

    document.documentElement.setAttribute('data-pswp-active', '');
    platePhotoSwipeTrigger = trigger;
    return platePhotoSwipe.loadAndOpen(index);
  } catch (error) {
    platePhotoSwipeModalCleanup?.(false);
    platePhotoSwipe = null;
    platePhotoSwipeLoading = null;
    platePhotoSwipeTrigger = null;
    document.documentElement.removeAttribute('data-pswp-active');
    console.warn('PhotoSwipe failed to load; using dialog viewer.', error);
    return false;
  }
}

function setupPlateViewer() {
  const viewer = document.querySelector('[data-plate-viewer]');
  if (!viewer || typeof viewer.showModal !== 'function') return;
  const figure = viewer.querySelector('[data-plate-viewer-figure]');
  const numberLabel = viewer.querySelector('[data-plate-viewer-number]');
  const captionLabel = viewer.querySelector('[data-plate-viewer-caption]');
  const previous = viewer.querySelector('[data-plate-viewer-prev]');
  const next = viewer.querySelector('[data-plate-viewer-next]');
  const close = viewer.querySelector('[data-plate-viewer-close]');
  const items = Array.from(document.querySelectorAll('[data-product-plate]'))
    .map((plate) => ({ plate, img: plate.querySelector('.plate__frame img') }))
    .filter((item) => item.img);
  if (!figure || !items.length) return;
  const image = document.createElement('img');
  image.setAttribute('data-plate-viewer-image', '');
  image.alt = '';
  figure.appendChild(image);

  let current = 0;
  let opener = null;

  const render = () => {
    const item = items[current];
    image.src = item.img.currentSrc || item.img.src;
    image.srcset = item.img.srcset || '';
    image.sizes = '92vw';
    image.alt = item.img.alt || '';
    numberLabel.textContent = `${padPlate(Number(item.plate.dataset.productPlate))} / ${padPlate(items.length)}`;
    const caption = item.plate.querySelector('[data-plate-caption-text]')?.textContent ?? '';
    captionLabel.textContent = caption;
    captionLabel.hidden = !caption;
    const single = items.length < 2;
    previous.hidden = single;
    next.hidden = single;
  };

  const step = (direction) => {
    current = (current + direction + items.length) % items.length;
    render();
  };

  document.querySelectorAll('[data-plate-open]').forEach((trigger) => {
    trigger.addEventListener('click', async () => {
      if (document.documentElement.hasAttribute('data-pswp-active') && window.pswp) return;
      if (typeof window.__IVG_LOAD_PSWP__ === 'function') {
        const opened = await openPlatePhotoSwipe(trigger);
        if (opened) return;
      }
      const plate = trigger.closest('[data-product-plate]');
      const index = items.findIndex((item) => item.plate === plate);
      if (index < 0) return;
      opener = trigger;
      current = index;
      render();
      viewer.showModal();
    });
  });

  previous.addEventListener('click', () => step(-1));
  next.addEventListener('click', () => step(1));
  close.addEventListener('click', () => viewer.close());
  viewer.addEventListener('click', (event) => {
    if (event.target === viewer) viewer.close();
  });
  viewer.addEventListener('close', () => (opener ?? document.querySelector('[data-plate-open]'))?.focus());
  viewer.addEventListener('keydown', (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'ArrowLeft') step(-1);
    if (event.key === 'ArrowRight') step(1);
  });
}

function setupPlateReveal() {
  const plates = document.querySelectorAll('.plate');
  if (!plates.length || !('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.documentElement.classList.add('plates-reveal');
  const show = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-seen');
      observer.unobserve(entry.target);
    });
  };
  const observer = new IntersectionObserver(show, { rootMargin: '0px 0px -8% 0px' });
  plates.forEach((plate) => observer.observe(plate));
}

function setupRecommendations() {
  const root = document.querySelector('[data-recommendations-root]');
  if (!root || !root.dataset.recommendationsUrl || root.children.length > 0) return;
  fetch(root.dataset.recommendationsUrl, { headers: { Accept: 'text/html' } })
    .then((response) => response.ok ? response.text() : '')
    .then((html) => {
      if (!html) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.querySelector('[data-recommendations-root]');
      if (replacement?.children.length) root.replaceWith(replacement);
    })
    .catch((error) => console.warn('Product recommendations unavailable.', error));
}

/* The hero canvas belongs entirely to assets/ivg-hero.js (built from src/hero): it creates its own
   <canvas>, gives it a WebGL2 context, and appends it to [data-hero-stage]. There is no <canvas> in
   the hero markup, so any canvas this file could find inside [data-hero] is ivg-hero's — asking
   that canvas for a second, different-type WebGL context fails ("Canvas has an existing context of
   a different type") and three.js logs it through its webglcontextcreationerror listener. The old
   string-glow renderer that lived here was the pre-three.js enhancement and is retired; ivg-hero
   also sets the hero's is-enhanced class. */

function setupSellUpload() {
  const form = document.querySelector('[data-sell-upload-form]');
  if (!form) return;

  const maxPhotoBytes = 20 * 1024 * 1024;
  const inputs = Array.from(form.querySelectorAll('[data-photo-input]'));
  const addPhoto = form.querySelector('[data-photo-add]');
  const photoError = form.querySelector('[data-photo-error]');
  const status = form.querySelector('.form-status');
  const statusMessage = status?.querySelector('[data-sell-status-message]');
  const cartLink = status?.querySelector('[data-sell-cart-link]');
  const progress = form.querySelector('[data-sell-progress]');
  const cartLinkLabel = cartLink?.textContent ?? '';
  let submitting = false;
  const setStatus = (message, showCartLink = false) => {
    if (!status) return;
    if (statusMessage) statusMessage.textContent = message;
    else status.textContent = message;
    if (cartLink) {
      cartLink.hidden = !showCartLink;
      cartLink.textContent = showCartLink ? cartLinkLabel : '';
    }
    status.hidden = !message;
  };
  const validatePhotos = () => {
    let firstInvalid = null;
    inputs.forEach((input) => {
      const oversized = Array.from(input.files ?? []).find((file) => file.size > maxPhotoBytes);
      if (oversized) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      if (!firstInvalid && oversized) firstInvalid = { input, file: oversized };
    });
    if (photoError) {
      photoError.textContent = firstInvalid
        ? `${firstInvalid.file.name} is larger than 20 MB. Choose a smaller photo.`
        : '';
      photoError.hidden = !firstInvalid;
    }
    return firstInvalid;
  };
  const syncPhotoInputs = () => {
    const next = inputs.find((input) => input.closest('[data-photo-row]')?.hidden);
    if (!next || inputs.every((input) => input.files?.length)) {
      if (addPhoto) addPhoto.hidden = true;
      return;
    }
    if (addPhoto) {
      addPhoto.hidden = false;
      const filled = inputs.filter((input) => input.files?.length).length;
      addPhoto.textContent = `${addPhoto.dataset.label ?? addPhoto.textContent} (${filled}/${inputs.length})`;
    }
  };
  if (addPhoto) {
    addPhoto.dataset.label = addPhoto.textContent;
    addPhoto.addEventListener('click', () => {
      const next = inputs.find((input) => input.closest('[data-photo-row]')?.hidden);
      if (!next) return;
      next.closest('[data-photo-row]').hidden = false;
      syncPhotoInputs();
      next.focus();
    });
  }
  inputs.forEach((input) => input.addEventListener('change', () => {
    validatePhotos();
    syncPhotoInputs();
  }));
  syncPhotoInputs();

  const contactForm = document.querySelector('[data-sell-contact-form]');
  let captchaReady = null;
  const protectContactForm = () => {
    if (!captchaReady) {
      captchaReady = new Promise((resolve) => {
        const protect = window.Shopify?.captcha?.protect;
        if (!contactForm || typeof protect !== 'function' || contactForm.dataset.hcaptchaBound) {
          resolve();
          return;
        }
        protect(contactForm, resolve);
        setTimeout(resolve, 8000);
      });
    }
    return captchaReady;
  };
  // Start loading the captcha as soon as the seller begins filling in the form.
  form.addEventListener('focusin', () => { protectContactForm(); }, { once: true });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!form.reportValidity()) return;
    const invalidPhoto = validatePhotos();
    if (invalidPhoto) {
      invalidPhoto.input.focus();
      return;
    }
    const submit = form.querySelector('[type="submit"]');
    if (!contactForm) return form.submit();
    setStatus('');
    submitting = true;
    const working = (label) => {
      if (progress) progress.textContent = label;
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = label;
      }
    };
    try {
      // Shopify stores line-item file properties on its CDN as soon as they are added to the cart
      // and returns their links. Only the photos go into the cart, and that one line is removed
      // straight away, so no order is created and any merchandise in the cart is left alone.
      const photoInputs = inputs.filter((input) => input.files?.length);
      let photoLinks = [];
      if (photoInputs.length) {
        working('Uploading photos…');
        const upload = new FormData();
        upload.append('id', form.querySelector('[name="id"]').value);
        upload.append('quantity', '1');
        photoInputs.forEach((input, index) => upload.append(`properties[Photo ${index + 1}]`, input.files[0]));
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: upload,
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => null);
          throw new Error(detail?.description || detail?.message || `Upload failed (${response.status}).`);
        }
        const line = await response.json();
        photoLinks = Object.entries(line.properties ?? {})
          .filter(([key, value]) => /^Photo \d+$/.test(key) && typeof value === 'string' && value)
          .map(([, value]) => value);
        if (photoLinks.length !== photoInputs.length) throw new Error('Some photos did not upload. Please try again.');
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        }).catch(() => null);
      }
      working('Sending…');
      const field = (name) => form.querySelector(`[name="properties[${name}]"]`)?.value.trim() ?? '';
      const fill = (key, value) => {
        const target = contactForm.querySelector(`[data-sell-contact="${key}"]`);
        if (target) target.value = value;
      };
      fill('name', field('Your name'));
      fill('email', field('Email'));
      fill('phone', field('Phone'));
      fill('body', field('Instrument details'));
      fill('photos', photoLinks.length ? photoLinks.map((link, index) => `${index + 1}. ${link}`).join('\n') : 'No photos attached');
      fill('page', window.location.href.split('#')[0]);
      // Shopify blocks contact-form submits until its captcha is bound to the form, and it only
      // binds on focus or change inside that form. This one has only hidden fields, so bind it here.
      await protectContactForm();
      if (typeof contactForm.requestSubmit === 'function') contactForm.requestSubmit();
      else contactForm.submit();
    } catch (error) {
      const networkFailure = error instanceof TypeError
        || /(?:networkerror|failed to fetch|load failed|network request failed)/i.test(error?.message ?? '');
      const message = networkFailure
        ? 'Connection unavailable. Check your internet connection and try again.'
        : error.message;
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        submit.textContent = submit.dataset.label ?? submit.textContent;
      }
      submitting = false;
      if (progress) progress.textContent = '';
      if (status) {
        setStatus(message, error.showCartLink);
        status.focus();
      } else {
        alert(message);
      }
    }
  });
  const submit = form.querySelector('[type="submit"]');
  if (submit) submit.dataset.label = submit.textContent;
  const posted = document.querySelector('[data-sell-posted]');
  if (posted) {
    form.hidden = true;
    posted.focus();
  }
}

function setupDescribedMedia() {
  document.querySelectorAll('[data-described-media-group]').forEach((group) => {
    const players = Array.from(group.querySelectorAll('video'));
    players.forEach((player) => player.addEventListener('play', () => {
      players.forEach((other) => {
        if (other !== player && !other.paused) other.pause();
      });
    }));
    const alternative = group.querySelector('[data-media-alternative]');
    const describedVideo = group.querySelector('[data-audio-described-video]');
    alternative?.addEventListener('toggle', () => {
      if (!alternative.open && describedVideo && !describedVideo.paused) describedVideo.pause();
    });
  });
}

if (typeof document !== 'undefined') {
  document.documentElement.classList.add('js');
  setupNavigation();
  setupDialogs();
  setupFacets();
  setupInquiryContext();
  setupInquiryValidation();
  setupProductForm();
  setupPlateViewer();
  setupPlateReveal();
  setupSellUpload();
  setupDescribedMedia();
  setupRecommendations();
}
